import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { computed, makeObservable, observable, runInAction, action, reaction } from "mobx";
import { Maybe } from "@/domains/common/types";
import { Uuid } from "@/domains/global/identifiers";
import { SessionObservable } from "@/store/sessions/SessionObservable";
import {
  MDSComboboxFetchDataHandler,
  MDSComboboxItem,
  MDSComboboxMention,
} from "@/mds/components/forms";
import { logger } from "@/modules/logger";
import { MemCommonMentionKind, syntaxTreeModule } from "@mem-labs/common-editor";
import { SearchSuggestion, SearchSuggestionType } from "@/domains/search";
import { objectModule } from "@/modules/object";
import { uuidModule } from "@/modules/uuid";
import { liveQuery, Subscription } from "dexie";
import {
  ChatModel,
  SESSION_DEFAULT_VARIANT,
  SessionEventModelData,
  SessionPreviousChats,
} from "@/store/sessions/types";
import { getChatModelFamily } from "@/domains/chat-model";
import { formatDateString, generateRecentDateString, isToday } from "@/domains/date/date";
import { DateTime } from "luxon";
import { groupLens } from "@/modules/timeline/group-lenses";
import { LensListItem } from "@/modules/timeline/types";
import { TrackedEvent, trackEvent, PaywallContext } from "@/domains/metrics";
import { truncate } from "@/modules/truncate";
import {
  MDSMenuContentItem,
  MDSMenuContentItemKind,
  MDSMenuContentList,
  MDSMenuItemVariant,
} from "@/mds";
import { INoteObservable } from "@/store/note";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { GatedFeature } from "@/store/feature-gates/types";
import { matchPath } from "react-router";
import { convertPathToStringWithoutTid } from "@/store/navigation/helpers";
import { STEP_IDS } from "@/store/getting-started";

// Common chat epochs for convenience when checking session age
export enum ChatEpoch {
  INACTIVE = 5 * 60 * 1000, // 5 minutes in ms
}

export class MemItPageStore extends AppSubStore {
  private sessionId: Uuid = uuidModule.generate();
  private liveQuerySubscription: Maybe<Subscription>;
  private subscribedPreviousSessions: SessionPreviousChats[] = [];
  private subscribedSessionEvents: SessionEventModelData[] = [];
  private subscribedSessionEventsLiveQuery: Maybe<Subscription>;

  // Mention management state
  private externalNoteObservables: Map<string, INoteObservable> = new Map();
  private externalCollectionObservables: Map<string, CollectionObservable> = new Map();
  private userAddedNoteObservables: Map<string, INoteObservable> = new Map();
  private userAddedCollectionObservables: Map<string, CollectionObservable> = new Map();

  // Implicit mention management state (tracks all visible panes)
  private implicitNoteObservables: Map<string, INoteObservable> = new Map();
  private implicitCollectionObservables: Map<string, CollectionObservable> = new Map();

  // Flag to indicate a new chat message is pending (used to hide empty state)
  isPendingNewChat: boolean = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      MemItPageStore,
      | "mountState"
      | "sessionId"
      | "liveQuerySubscription"
      | "initializeLiveQuery"
      | "subscribedPreviousSessions"
      | "prepareNoteMetadata"
      | "prepareCollectionMetadata"
      | "subscribedSessionEvents"
      | "subscribedSessionEventsLiveQuery"
      | "initializeSessionEventsLiveQuery"
      | "createHeader"
      | "processRecentItem"
      | "processSearchSuggestion"
      | "buildDataWithHeaders"
      | "externalNoteObservables"
      | "externalCollectionObservables"
      | "userAddedNoteObservables"
      | "userAddedCollectionObservables"
      | "implicitNoteObservables"
      | "implicitCollectionObservables"
      | "addExternalNoteMention"
      | "addExternalCollectionMention"
      | "addUserNoteMention"
      | "addUserCollectionMention"
      | "removeNoteMention"
      | "removeCollectionMention"
      | "clearAllMentions"
      | "clearUserMentions"
      | "clearExternalMentions"
      | "processPathForMentions"
      | "initializeCurrentObservableFromRoute"
      | "initializeRouteReaction"
      | "updateImplicitMentionsFromAllPanes"
      | "handleMentionAdd"
      | "handleMentionRemove"
      | "handleMentionsClear"
      | "handleModelSelect"
      | "handlePremiumModelClick"
      | "handleNewChatWithModel"
    >(this, {
      processPathForMentions: true,
      addExternalNoteMention: action,
      addExternalCollectionMention: action,
      addUserNoteMention: action,
      addUserCollectionMention: action,
      removeNoteMention: action,
      removeCollectionMention: action,
      clearAllMentions: action,
      clearUserMentions: action,
      clearExternalMentions: action,
      currentMentions: computed,
      currentImplicitMentions: computed,
      createHeader: true,
      processRecentItem: true,
      processSearchSuggestion: true,
      buildDataWithHeaders: true,
      initializeSessionEventsLiveQuery: true,
      resumeActiveSession: true,
      prepareNoteMetadata: true,
      prepareCollectionMetadata: true,
      handleNewSession: true,
      handleSelectSession: true,
      previousChatSessions: true,
      subscribedPreviousSessions: observable,
      subscribedSessionEvents: observable,
      liveQuerySubscription: observable,
      initializeLiveQuery: true,
      sessionId: observable,
      setSessionId: action,
      getPlaceholderSessionId: true,
      hasAged: false,
      mountState: false,
      session: computed,
      sessionEvents: computed,
      previousSessionsContentList: computed,
      handleComboboxFetch: false,
      handleCreateUserMessage: false,
      provideFeedback: false,
      subscribedSessionEventsLiveQuery: false,
      externalNoteObservables: observable,
      externalCollectionObservables: observable,
      userAddedNoteObservables: observable,
      userAddedCollectionObservables: observable,
      implicitNoteObservables: observable,
      implicitCollectionObservables: observable,
      isPendingNewChat: observable,
      selectedModel: computed,
      isPaidUser: computed,
      canChangeModel: computed,
      handleModelSelect: action,
      handlePremiumModelClick: action,
      handleNewChatWithModel: action,
      initializeCurrentObservableFromRoute: action,
      initializeRouteReaction: true,
      allVisiblePanePaths: computed,
      updateImplicitMentionsFromAllPanes: action,
      handleMentionAdd: action,
      handleMentionRemove: action,
      handleMentionsClear: action,
      setIsPendingNewChat: action,
      clearIsPendingNewChat: action,
    });

    this.initializeLiveQuery();
    this.initializeSessionEventsLiveQuery();
    this.initializeRouteReaction();
  }

  /**
   * Returns true if the active Mem It session's last user action
   * occurred earlier than `thresholdMs` milliseconds ago.
   * If there is no last action timestamp, returns true.
   */
  hasAged(thresholdMs: number): boolean {
    const last = this.session?.lastUserActionAt;
    if (!last) return true;

    const lastAt = new Date(last).getTime();
    if (Number.isNaN(lastAt)) return false;

    return Date.now() - lastAt > thresholdMs;
  }

  get session(): Maybe<SessionObservable> {
    return this.store.sessions.get(this.sessionId);
  }

  get sessionEvents(): SessionEventModelData[] {
    if (!this.session) return [];

    return this.subscribedSessionEvents;
  }

  get previousChatSessions(): SessionPreviousChats[] {
    return this.subscribedPreviousSessions;
  }

  /**
   * Computed dropdown content list for previous sessions, grouped by date
   */
  get previousSessionsContentList(): MDSMenuContentList {
    // Transform sessions into lens items for grouping
    const lensItems: LensListItem<SessionPreviousChats>[] = this.subscribedPreviousSessions
      // Skip the current session when building the dropdown list
      .map(session => ({
        dateTime: new Date(session.lastUserActionAt), // We expect previousChatSessions to have been filtered out items with no last_user_action_at already
        item: session,
      }));

    // Group sessions by date ranges using the reusable timeline grouping logic
    const groupedSessions = groupLens(lensItems);

    const items: MDSMenuContentItem[] = [];

    groupedSessions.forEach((group, groupIndex) => {
      // Add divider before each group except the first
      if (groupIndex > 0) {
        items.push({
          id: `${group.title}-separator`,
          kind: MDSMenuContentItemKind.Divider,
        });
      }

      // Add group header
      items.push({
        id: group.title,
        kind: MDSMenuContentItemKind.Header,
        label: group.title,
      });

      // Add session items in this group
      group.items.forEach(({ dateTime, item }) => {
        const dTime = DateTime.fromJSDate(dateTime);
        items.push({
          id: item.modelId,
          kind: MDSMenuContentItemKind.Item,
          variant: MDSMenuItemVariant.TwoLine,
          label: item.title,
          subtitle: isToday(dTime)
            ? formatDateString(dTime, "h:mm a")
            : formatDateString(dTime, "MMM dd"),
          onClick: () => this.handleSelectSession(item.modelId),
        });
      });
    });

    return items;
  }

  get selectedModel(): ChatModel {
    const variant = this.session?.modelData.config?.variant;
    switch (variant) {
      case "CLAUDE_SONNET_4_5":
        return ChatModel.Sonnet45;
      case "CLAUDE_SONNET_4_6":
        return ChatModel.Sonnet;
      case "GPT_5_2":
        return ChatModel.GPT;
      case "GPT_5_3":
        return ChatModel.GPT53;
      case "GPT_5_4":
        return ChatModel.GPT54;
      case "GEMINI_3_FLASH":
        return ChatModel.Gemini;
      case "GEMINI_3_1_PRO":
        return ChatModel.Gemini31Pro;
      default:
        return ChatModel.Auto;
    }
  }

  /**
   * Sets the current session ID. The session should already exist in the database
   * either from being created by getLatestInitialChatSession or loaded from URL.
   */
  setSessionId(sessionId: Uuid) {
    this.sessionId = sessionId;
  }

  /**
   * Gets a placeholder session ID for use as the next new session.
   * Uses the latest INITIAL session or creates one if none exists.
   */
  public async getPlaceholderSessionId(): Promise<Uuid> {
    return this.store.sessions.getLatestInitialChatSession();
  }

  public handleSelectSession = (sessionId: Uuid) => {
    // Use non-persistent navigation for manual chat selections
    this.store.sidePanel.goToMemIt(sessionId);
  };

  public handleNewSession = async () => {
    const newSessionId = await this.getPlaceholderSessionId();
    this.handleSelectSession(newSessionId);
    return newSessionId;
  };

  /**
   * Resume most recent session if active within 5 minutes
   */
  async resumeActiveSession(): Promise<string | undefined> {
    // Collect most recent session from previous chats
    const prevChatSessions = await this.store.sessions.getPreviousChatSessions();
    const mostRecentSession = prevChatSessions[0];

    if (!mostRecentSession || !mostRecentSession.lastUserActionAt) {
      return undefined;
    }

    const lastUserActivityAt = DateTime.fromISO(mostRecentSession.lastUserActionAt);
    const fiveMinutesAgo = DateTime.now().minus({ minutes: 5 });

    if (lastUserActivityAt > fiveMinutesAgo) {
      return mostRecentSession.modelId;
    }

    return undefined;
  }

  private prepareCollectionMetadata = async (collection: CollectionObservable) => {
    const noteCount = (await collection.itemList.getAllCollectionItemIdsAsync()).length;
    return {
      collectionId: collection.id,
      isShared: collection.isShared,
      description: collection.description,
      noteCount,
    };
  };

  private prepareNoteMetadata = (note: INoteObservable) => {
    return {
      isShared: note.isShared,
      dateLabel: generateRecentDateString(
        DateTime.fromISO(
          note.lastMentionedAt ||
            note.lastViewedAt ||
            note.receivedAt ||
            note.locallyCreatedAt ||
            ""
        ),
        { skipFullDayName: true }
      ),
    };
  };

  private createHeader = (type: SearchSuggestionType): MDSComboboxItem => ({
    id: type === SearchSuggestionType.NOTE ? "notes-header" : "collections-header",
    label: type === SearchSuggestionType.NOTE ? "Notes" : "Collections",
    kind:
      type === SearchSuggestionType.NOTE
        ? MemCommonMentionKind.Note
        : MemCommonMentionKind.Collection,
    isHeader: true,
  });

  private processRecentItem = async (
    item: INoteObservable | CollectionObservable
  ): Promise<MDSComboboxItem> => ({
    id: item.id,
    label: item instanceof CollectionObservable ? item.label : item.title,
    kind:
      item instanceof CollectionObservable
        ? MemCommonMentionKind.Collection
        : MemCommonMentionKind.Note,
    metadata: {
      ...(item instanceof CollectionObservable
        ? await this.prepareCollectionMetadata(item)
        : this.prepareNoteMetadata(item)),
    },
  });

  private processSearchSuggestion = async (
    suggestion: SearchSuggestion
  ): Promise<MDSComboboxItem> => {
    const item =
      suggestion.type === SearchSuggestionType.NOTE
        ? await this.store.notes.getAsync(suggestion.modelId)
        : await this.store.collections.getAsync(suggestion.modelId);

    return {
      id: suggestion.modelId,
      label: suggestion.label || suggestion.modelId,
      value: suggestion.label || suggestion.modelId,
      kind:
        suggestion.type === SearchSuggestionType.NOTE
          ? MemCommonMentionKind.Note
          : MemCommonMentionKind.Collection,
      metadata: {
        ...(item instanceof CollectionObservable
          ? await this.prepareCollectionMetadata(item)
          : this.prepareNoteMetadata(item as INoteObservable)),
      },
    };
  };

  private buildDataWithHeaders = (
    notesData: MDSComboboxItem[],
    collectionsData: MDSComboboxItem[]
  ): MDSComboboxItem[] => {
    const data: MDSComboboxItem[] = [];

    if (notesData.length > 0) {
      data.push(this.createHeader(SearchSuggestionType.NOTE));
      data.push(...notesData);
    }

    if (collectionsData.length > 0) {
      data.push(this.createHeader(SearchSuggestionType.COLLECTION));
      data.push(...collectionsData);
    }

    return data;
  };

  handleComboboxFetch: MDSComboboxFetchDataHandler<MDSComboboxItem> = async ({
    query,
    mentionSymbol,
  }) => {
    try {
      const suggestionType =
        mentionSymbol === "@"
          ? SearchSuggestionType.NOTE
          : mentionSymbol === "#"
            ? SearchSuggestionType.COLLECTION
            : undefined;

      // Handle empty query: return recents
      if (!query) {
        if (suggestionType) {
          const recentItems = await Promise.all(
            (suggestionType === SearchSuggestionType.NOTE
              ? this.store.recentItems.sortedRecentNotesInteractedWithByMe
              : this.store.recentItems.sortedRecentCollectionsInteractedWithByMe
            )
              .filter(item => item.isAvailable)
              .slice(0, 5)
              .map(this.processRecentItem)
          );

          const data =
            recentItems.length > 0 ? [this.createHeader(suggestionType), ...recentItems] : [];

          return { data, isLoading: false };
        } else {
          const recentNotes = await Promise.all(
            this.store.recentItems.sortedRecentNotesInteractedWithByMe
              .filter(item => item.isAvailable)
              .slice(0, 5)
              .map(this.processRecentItem)
          );

          const recentCollections = await Promise.all(
            this.store.recentItems.sortedRecentCollectionsInteractedWithByMe
              .filter(item => item.isAvailable)
              .slice(0, 5)
              .map(this.processRecentItem)
          );

          return {
            data: this.buildDataWithHeaders(recentNotes, recentCollections),
            isLoading: false,
          };
        }
      }

      // Handle search query
      const andCond = suggestionType
        ? (item: SearchSuggestion) => item.type === suggestionType
        : undefined;

      const suggestions = await this.store.search.forSuggestions(query, "mentions", andCond, 5);

      if (suggestionType) {
        const searchResults = await Promise.all(suggestions.map(this.processSearchSuggestion));
        const data =
          searchResults.length > 0 ? [this.createHeader(suggestionType), ...searchResults] : [];

        return { data, isLoading: false };
      } else {
        const notesSuggestions = suggestions.filter(s => s.type === SearchSuggestionType.NOTE);
        const collectionsSuggestions = suggestions.filter(
          s => s.type === SearchSuggestionType.COLLECTION
        );

        const notesData = await Promise.all(notesSuggestions.map(this.processSearchSuggestion));
        const collectionsData = await Promise.all(
          collectionsSuggestions.map(this.processSearchSuggestion)
        );

        return {
          data: this.buildDataWithHeaders(notesData, collectionsData),
          isLoading: false,
        };
      }
    } catch (error) {
      logger.error({
        message: "Failed to fetch combobox data",
        info: {
          error: error instanceof Error ? error.message : String(error),
          query: String(query),
        },
      });
      return { data: [], isLoading: false };
    }
  };

  handleCreateUserMessage = async (payload: {
    content: string;
    context?: Record<string, unknown>;
  }): Promise<boolean> => {
    if (!this.session) {
      logger.warn({
        message: "Cannot create user message: no session available",
      });
      return false;
    }

    // if the user is out of chat messages, open the upsell and return false
    const chatMessageFeatureGate = await this.store.featureGates.checkGate(
      GatedFeature.ChatMessageSend
    );

    if (!chatMessageFeatureGate.allowed) {
      this.store.featureGates.openUsageLimitUpsell({
        used: chatMessageFeatureGate.used,
        limit: chatMessageFeatureGate.limit,
        feature: GatedFeature.ChatMessageSend,
        context: PaywallContext.ChatMessageSend,
      });
      return false;
    }

    try {
      // Mark send message step as complete in getting started guide
      this.store.gettingStarted?.markStepComplete(STEP_IDS.SEND_CHAT_MESSAGE);

      // Create the user message event
      await this.store.sessions.createUserMessageEvent({
        sessionId: this.session.id,
        content: payload.content,
        context: payload.context,
      });

      // Track the send message event
      trackEvent(TrackedEvent.ChatSessionMessageSend, {
        session_id: this.session.id,
        message_contents: payload.content,
        selectedModel: this.selectedModel,
        model_family: getChatModelFamily(this.selectedModel),
      });

      if (this.session.status !== "INITIAL") {
        // Update lastUserActionAt but not the title (we assume it was set by first message)
        await this.store.sessions.updateSessionOnNewUserMessage(this.session.id, {
          lastUserActionAt: new Date().toISOString(),
        });
        logger.debug({
          message: "Session is not in INITIAL state, skipping creation",
          info: {
            sessionId: this.session.id,
            content: payload.content,
          },
        });
        return true;
      }

      // Update session title with the message content (plain text)
      const plainText = syntaxTreeModule.convertMdxToPlaintext({ mdxContent: payload.content });
      const title = truncate(plainText, 50) || "New Chat";

      // Update session title and lastUserActionAt
      await this.store.sessions.updateSessionOnNewUserMessage(this.session.id, {
        title, // Set title as first message
        lastUserActionAt: new Date().toISOString(),
      });
      // Immediately create a new session to always have a ready placeholder session
      // Note that we reuse the same variant (LLM model) as is on this session.
      // Since we just created user message, its safe to assume it's the latest requested model
      let currentVariant = this.session.modelData.config?.variant || SESSION_DEFAULT_VARIANT;
      if (currentVariant === "CLAUDE_SONNET_4_5") {
        currentVariant = "CLAUDE_SONNET_4_6";
      }
      await this.store.sessions.createSession({
        status: "INITIAL",
        kind: "GENERIC",
        config: { schema_version: "V2", variant: currentVariant, enable_agent_digests: true },
      });

      logger.debug({
        message: "User message created successfully",
        info: {
          sessionId: this.session.id,
          content: payload.content,
          sessionTitle: title,
          sessionStatus: "PROCESSING",
        },
      });

      return true;
    } catch (error) {
      logger.error({
        message: "Failed to create user message",
        info: {
          error: objectModule.safeErrorAsJson(error as Error),
          content: payload.content,
          hasContext: payload.context !== undefined,
        },
      });

      return false;
    }
  };

  /**
   * Provide feedback for a MemIt session event
   */
  async provideFeedback(
    eventId: string,
    responseKind: "UPVOTE" | "DOWNVOTE",
    userText?: string,
    selectedSuggestions?: string[]
  ) {
    const sessionEvent = await this.store.sessionEvents.getAsync(eventId);
    if (!sessionEvent) {
      logger.warn({
        message: "[memit] Cannot provide feedback: session event not found",
        info: { eventId },
      });
      return;
    }

    try {
      await this.store.feedback.provideFeedback({
        feedbackKind: "MEM_IT_RESPONSE",
        associatedModelId: eventId,
        associatedModelKind: "SESSION_EVENT",
        associatedContext: {
          session_id: sessionEvent.modelData.session_id,
          event_kind: sessionEvent.modelData.kind,
        },
        feedbackResponseKind: responseKind,
        feedbackUserText: userText,
        feedbackSelectedSuggestions: selectedSuggestions,
        feedbackRespondedAt: new Date().toISOString(),
      });

      logger.debug({
        message: "[memit]MemIt feedback provided successfully",
        info: {
          eventId,
          responseKind,
          sessionId: sessionEvent.modelData.session_id,
          hasUserText: !!userText,
          hasSelectedSuggestions: !!selectedSuggestions?.length,
        },
      });

      if (responseKind === "UPVOTE") {
        trackEvent(TrackedEvent.ChatSessionMessageUpvote, {
          event_id: eventId,
          session_id: sessionEvent.modelData.session_id,
          event_kind: sessionEvent.modelData.kind,
          selected_model: this.selectedModel,
        });
      } else if (responseKind === "DOWNVOTE") {
        trackEvent(TrackedEvent.ChatSessionMessageDownvote, {
          event_id: eventId,
          session_id: sessionEvent.modelData.session_id,
          event_kind: sessionEvent.modelData.kind,
          selected_model: this.selectedModel,
        });
      }
    } catch (error) {
      logger.error({
        message: "[memit]Failed to provide MemIt feedback",
        info: {
          eventId,
          responseKind,
          error: objectModule.safeErrorAsJson(error as Error),
        },
      });
      throw error;
    }
  }

  private initializeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(() => {
      return this.store.sessions.getPreviousChatSessions();
    }).subscribe({
      next: rows => {
        runInAction(() => {
          this.subscribedPreviousSessions = rows;
        });
      },
    });
  }

  private initializeSessionEventsLiveQuery() {
    reaction(
      () => this.sessionId,
      () => {
        this.subscribedSessionEventsLiveQuery?.unsubscribe();
        this.subscribedSessionEventsLiveQuery = liveQuery(() => {
          return this.store.sessionEvents.getManySessionEventsBySessionId(this.sessionId);
        }).subscribe({
          next: rows => {
            runInAction(() => {
              this.subscribedSessionEvents = rows;
            });
          },
        });
      },
      { fireImmediately: true }
    );
  }

  private initializeRouteReaction() {
    reaction(
      () => this.store.navigation.activePath,
      () => {
        this.initializeCurrentObservableFromRoute();
      },
      { fireImmediately: true }
    );

    // Track all panes for implicit mentions
    reaction(
      () => this.allVisiblePanePaths,
      () => {
        // Call async method without awaiting to avoid blocking the reaction
        this.updateImplicitMentionsFromAllPanes();
      },
      { fireImmediately: true }
    );
  }

  get allVisiblePanePaths() {
    const navigationTabs = this.store.navigation.tabs;
    const allPanes = Array.from(navigationTabs.panesMap.values());

    const paths = allPanes
      .map(pane => {
        if (!pane.activeTabId) return null;
        const activeTab = navigationTabs.tabsMap.get(pane.activeTabId);
        const path = activeTab?.path || null;
        return path;
      })
      .filter((path): path is string => Boolean(path));

    return paths;
  }

  /**
   * Common logic to process a path and extract note/collection matches
   */
  private async processPathForMentions(path: string): Promise<{
    noteObservable?: INoteObservable;
    collectionObservable?: CollectionObservable;
  }> {
    const resolvedPath = convertPathToStringWithoutTid(path);

    // Check for note route
    const noteMatch = matchPath("/notes/:noteId", resolvedPath);
    if (noteMatch?.params.noteId) {
      const noteId = noteMatch.params.noteId;
      const noteObservable = await this.store.notes.getAsync(noteId);
      if (noteObservable) {
        return { noteObservable };
      }
    }

    // Check for collection route
    const collectionMatch = matchPath("/collections/:collectionId", resolvedPath);
    if (collectionMatch?.params.collectionId) {
      const collectionId = collectionMatch.params.collectionId;
      const collectionObservable = await this.store.collections.getAsync(collectionId);
      if (collectionObservable) {
        return { collectionObservable };
      }
    }

    return {};
  }

  updateImplicitMentionsFromAllPanes = async () => {
    // Clear previous implicit mentions
    this.implicitNoteObservables.clear();
    this.implicitCollectionObservables.clear();

    const visiblePaths = this.allVisiblePanePaths;

    // Process all paths asynchronously to handle note loading
    await Promise.all(
      visiblePaths.map(async path => {
        if (!path) return;

        const { noteObservable, collectionObservable } = await this.processPathForMentions(path);

        if (noteObservable) {
          runInAction(() => {
            this.implicitNoteObservables.set(noteObservable.id, noteObservable);
          });
        }

        if (collectionObservable) {
          runInAction(() => {
            this.implicitCollectionObservables.set(collectionObservable.id, collectionObservable);
          });
        }
      })
    );
  };

  initializeCurrentObservableFromRoute = async () => {
    const activePath = this.store.navigation.activePath;

    if (!activePath) {
      this.clearExternalMentions();
      return;
    }

    // Clear only external mentions, preserve user-added ones
    this.clearExternalMentions();

    // Convert activePath to string format for processing
    const pathString = typeof activePath === "string" ? activePath : activePath.pathname;
    if (!pathString) {
      return;
    }

    const { noteObservable, collectionObservable } = await this.processPathForMentions(pathString);

    if (noteObservable) {
      this.addExternalNoteMention(noteObservable);
    }

    if (collectionObservable) {
      this.addExternalCollectionMention(collectionObservable);
    }
  };

  addExternalNoteMention = (noteObservable: INoteObservable) => {
    const noteId = noteObservable.id;

    // Remove any existing user-added mention with the same ID (external takes precedence)
    if (this.userAddedNoteObservables.has(noteId)) {
      this.userAddedNoteObservables.delete(noteId);
    }

    // Add to external mentions map
    this.externalNoteObservables.set(noteId, noteObservable);
  };

  addExternalCollectionMention = (collectionObservable: CollectionObservable) => {
    const collectionId = collectionObservable.id;

    // Remove any existing user-added mention with the same ID (external takes precedence)
    if (this.userAddedCollectionObservables.has(collectionId)) {
      this.userAddedCollectionObservables.delete(collectionId);
    }

    // Add to external mentions map
    this.externalCollectionObservables.set(collectionId, collectionObservable);
  };

  addUserNoteMention = (noteObservable: INoteObservable) => {
    const noteId = noteObservable.id;

    // Don't add user mention if external mention with same ID already exists
    if (this.externalNoteObservables.has(noteId)) {
      return; // External takes precedence
    }

    // Add to user-added mentions map
    this.userAddedNoteObservables.set(noteId, noteObservable);
  };

  addUserCollectionMention = (collectionObservable: CollectionObservable) => {
    const collectionId = collectionObservable.id;

    // Don't add user mention if external mention with same ID already exists
    if (this.externalCollectionObservables.has(collectionId)) {
      return; // External takes precedence
    }

    // Add to user-added mentions map
    this.userAddedCollectionObservables.set(collectionId, collectionObservable);
  };

  removeNoteMention = (noteId: string) => {
    // Remove from both maps
    this.externalNoteObservables.delete(noteId);
    this.userAddedNoteObservables.delete(noteId);
  };

  removeCollectionMention = (collectionId: string) => {
    // Remove from both maps
    this.externalCollectionObservables.delete(collectionId);
    this.userAddedCollectionObservables.delete(collectionId);
  };

  clearAllMentions = () => {
    // Clear all mentions maps
    this.externalNoteObservables.clear();
    this.externalCollectionObservables.clear();
    this.userAddedNoteObservables.clear();
    this.userAddedCollectionObservables.clear();
  };

  clearUserMentions = () => {
    // Clear user-added mentions maps only
    this.userAddedNoteObservables.clear();
    this.userAddedCollectionObservables.clear();
  };

  clearExternalMentions = () => {
    // Clear external mentions maps only
    this.externalNoteObservables.clear();
    this.externalCollectionObservables.clear();
  };

  get currentMentions(): MDSComboboxMention[] {
    const mentions: MDSComboboxMention[] = [];

    // Add all external notes
    this.externalNoteObservables.forEach(noteObservable => {
      mentions.push({
        kind: MemCommonMentionKind.Note,
        isExternal: true,
        note_id: noteObservable.id,
        note_primary_label: noteObservable.title || "",
      });
    });

    // Add all external collections
    this.externalCollectionObservables.forEach(collectionObservable => {
      mentions.push({
        kind: MemCommonMentionKind.Collection,
        isExternal: true,
        collection_id: collectionObservable.id,
        collection_title: collectionObservable.label || "",
      });
    });

    // Add all user-added notes
    this.userAddedNoteObservables.forEach(noteObservable => {
      mentions.push({
        kind: MemCommonMentionKind.Note,
        isExternal: false,
        note_id: noteObservable.id,
        note_primary_label: noteObservable.title || "",
      });
    });

    // Add all user-added collections
    this.userAddedCollectionObservables.forEach(collectionObservable => {
      mentions.push({
        kind: MemCommonMentionKind.Collection,
        isExternal: false,
        collection_id: collectionObservable.id,
        collection_title: collectionObservable.label || "",
      });
    });

    return mentions;
  }

  get currentImplicitMentions(): MDSComboboxMention[] {
    const mentions: MDSComboboxMention[] = [];

    // Add all implicit notes
    this.implicitNoteObservables.forEach(noteObservable => {
      mentions.push({
        kind: MemCommonMentionKind.Note,
        isExternal: true, // Mark as external to indicate non-removable
        note_id: noteObservable.id,
        note_primary_label: noteObservable.title || "",
      });
    });

    // Add all implicit collections
    this.implicitCollectionObservables.forEach(collectionObservable => {
      mentions.push({
        kind: MemCommonMentionKind.Collection,
        isExternal: true, // Mark as external to indicate non-removable
        collection_id: collectionObservable.id,
        collection_title: collectionObservable.label || "",
      });
    });

    return mentions;
  }

  // Handlers for combobox mention interactions
  handleMentionAdd = async (mention: MDSComboboxMention) => {
    if (mention.kind === MemCommonMentionKind.Note) {
      const noteObservable = this.store.notes.getNoteObservableById({ noteId: mention.note_id });
      if (noteObservable) {
        this.addUserNoteMention(noteObservable);
      }

      return;
    }

    if (mention.kind === MemCommonMentionKind.Collection) {
      const collectionObservable = this.store.collections.getCollectionObservableById({
        collectionId: mention.collection_id,
      });

      if (collectionObservable) {
        this.addUserCollectionMention(collectionObservable);
      }
    }
  };

  handleMentionRemove = (mentionId: string) => {
    // Check if it's a note mention (in either map)
    if (
      this.externalNoteObservables.has(mentionId) ||
      this.userAddedNoteObservables.has(mentionId)
    ) {
      this.removeNoteMention(mentionId);

      return;
    }

    // Check if it's a collection mention (in either map)
    if (
      this.externalCollectionObservables.has(mentionId) ||
      this.userAddedCollectionObservables.has(mentionId)
    ) {
      this.removeCollectionMention(mentionId);
    }
  };

  handleMentionsClear = () => {
    // Only clear user-added mentions, keep external (route-based) mentions
    this.clearUserMentions();
  };

  setIsPendingNewChat = (isPending: boolean) => {
    this.isPendingNewChat = isPending;
  };

  clearIsPendingNewChat = () => {
    this.isPendingNewChat = false;
  };

  /**
   * Returns true if the user has a paid subscription
   */
  get isPaidUser(): boolean {
    return this.store.subscriptions.personalSubscription?.isPaid ?? false;
  }

  /**
   * Returns true if the model can be changed (only when session is in INITIAL state)
   */
  get canChangeModel(): boolean {
    return this.session?.status === "INITIAL";
  }

  /**
   * Handle model selection from the dropdown
   * Updates all INITIAL sessions with the new model variant
   */
  handleModelSelect = async (variant: ChatModel) => {
    // Update all INITIAL sessions with the new model variant
    // ChatModel values map directly to SessionConfigVariant values
    await this.store.sessions.updateInitialSessionVariant(variant);

    logger.debug({
      message: "Model selected for chat",
      info: { variant, isPaidUser: this.isPaidUser },
    });
  };

  /**
   * Handle click on premium model when user is not paid
   * Opens the upsell modal
   */
  handlePremiumModelClick = () => {
    this.store.subscriptions.openUsageLimitUpsell({
      feature: GatedFeature.ChatMessageSend,
      context: PaywallContext.ChatModelSelection,
    });

    logger.debug({
      message: "Premium model clicked by free user, showing upsell",
    });
  };

  /**
   * Handle starting a new chat with a specific model
   * Used when user selects a model but the current session is already in progress
   */
  handleNewChatWithModel = async (variant: ChatModel) => {
    // Update all INITIAL sessions with the new model variant before creating new session
    await this.store.sessions.updateInitialSessionVariant(variant);

    // Then start a new chat session
    await this.handleNewSession();

    logger.debug({
      message: "New chat started with selected model",
      info: { variant, isPaidUser: this.isPaidUser },
    });
  };
}
